import { ChipsSection } from './chips-showcase';
import { ComboboxSection } from './combobox-showcase';
import { BadgesSection, ButtonsSection, FormControlsSection } from './controls';
import { PatternsSection, SemanticStatesSection, UsageGuideSection } from './semantic';

interface Swatch {
  label: string;
  cssVar: string;
}

interface SwatchSection {
  heading: string;
  description?: string;
  columns: 'scale' | 'roles';
  swatches: Swatch[];
}

const SCALE_STOPS = ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'] as const;

const SECTIONS: SwatchSection[] = [
  {
    heading: 'Primary scale',
    description: 'Metycle dark teal — full 10-stop scale.',
    columns: 'scale',
    swatches: SCALE_STOPS.map((stop) => ({
      label: `primary-${stop}`,
      cssVar: `primary-${stop}`,
    })),
  },
  {
    heading: 'Secondary scale',
    description: 'Metycle mint — full 10-stop scale.',
    columns: 'scale',
    swatches: SCALE_STOPS.map((stop) => ({
      label: `secondary-${stop}`,
      cssVar: `secondary-${stop}`,
    })),
  },
  {
    heading: 'Brand role tokens',
    description: 'The semantic role each scale resolves to by default.',
    columns: 'roles',
    swatches: [
      { label: 'primary', cssVar: 'primary' },
      { label: 'secondary', cssVar: 'secondary' },
      { label: 'tertiary', cssVar: 'tertiary' },
      { label: 'neutral', cssVar: 'neutral' },
    ],
  },
  {
    heading: 'Surface roles',
    description: 'Backgrounds, text, borders, and the focus ring.',
    columns: 'roles',
    swatches: [
      { label: 'background', cssVar: 'background' },
      { label: 'foreground', cssVar: 'foreground' },
      { label: 'card', cssVar: 'card' },
      { label: 'popover', cssVar: 'popover' },
      { label: 'border', cssVar: 'border' },
      { label: 'input', cssVar: 'input' },
      { label: 'ring', cssVar: 'ring' },
    ],
  },
  {
    heading: 'Sidebar tokens',
    description: 'Tokens consumed by the layout-skeleton sidebar.',
    columns: 'roles',
    swatches: [
      { label: 'sidebar-background', cssVar: 'sidebar-background' },
      { label: 'sidebar-foreground', cssVar: 'sidebar-foreground' },
      { label: 'sidebar-primary', cssVar: 'sidebar-primary' },
      { label: 'sidebar-accent', cssVar: 'sidebar-accent' },
      { label: 'sidebar-border', cssVar: 'sidebar-border' },
      { label: 'sidebar-ring', cssVar: 'sidebar-ring' },
    ],
  },
];

const TYPOGRAPHY = [
  { weight: 400, label: 'DM Sans Regular (400)' },
  { weight: 500, label: 'DM Sans Medium (500)' },
  { weight: 600, label: 'DM Sans SemiBold (600)' },
  { weight: 700, label: 'DM Sans Bold (700)' },
];

const RADIUS_SAMPLES = [
  { className: 'rounded-sm', token: '--radius-sm' },
  { className: 'rounded-md', token: '--radius-md' },
  { className: 'rounded-lg', token: '--radius-lg' },
  { className: 'rounded-xl', token: '--radius-xl' },
];

function SwatchTile({ swatch }: Readonly<{ swatch: Swatch }>) {
  return (
    <div className="space-y-1">
      <div
        className="border-border h-16 w-full rounded-md border"
        style={{ backgroundColor: `hsl(var(--${swatch.cssVar}))` }}
        aria-hidden
      />
      <p className="text-foreground font-mono text-xs">{swatch.label}</p>
    </div>
  );
}

function SwatchSectionView({ section }: Readonly<{ section: SwatchSection }>) {
  const gridClass =
    section.columns === 'scale'
      ? 'grid grid-cols-2 gap-3 sm:grid-cols-5 md:grid-cols-10'
      : 'grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4';
  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">{section.heading}</h2>
        {section.description && (
          <p className="text-muted-foreground mt-1 text-sm">{section.description}</p>
        )}
      </div>
      <div className={gridClass}>
        {section.swatches.map((swatch) => (
          <SwatchTile key={swatch.cssVar} swatch={swatch} />
        ))}
      </div>
    </section>
  );
}

function TypographySection() {
  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Typography</h2>
        <p className="text-muted-foreground mt-1 text-sm">
          DM Sans is loaded from Google Fonts at the four documented weights with the variable
          optical-size axis (9..40).
        </p>
      </div>
      <div className="space-y-3">
        {TYPOGRAPHY.map(({ weight, label }) => (
          <div key={weight} className="space-y-1">
            <p className="text-muted-foreground font-mono text-xs">{label}</p>
            <p className="text-foreground text-2xl" style={{ fontWeight: weight }}>
              The quick brown fox jumps over the lazy dog — 0123456789
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

function RadiusSection() {
  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Radius</h2>
        <p className="text-muted-foreground mt-1 text-sm">
          Single base radius (<code className="font-mono">0.75rem</code>) with three derived stops.
          Cards, controls, and popovers all consume one of these.
        </p>
      </div>
      <div className="flex flex-wrap gap-4">
        {RADIUS_SAMPLES.map((sample) => (
          <div
            key={sample.token}
            className={`bg-primary text-primary-foreground flex h-20 w-32 flex-col items-center justify-center text-xs ${sample.className}`}
          >
            <span className="font-mono">{sample.token}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function PageHeader() {
  return (
    <header className="space-y-2">
      <h1 className="text-2xl font-semibold">Design Tokens</h1>
      <p className="text-muted-foreground max-w-2xl text-sm">
        Visual reference for the foundry token catalogue. Every value here is sourced from{' '}
        <code className="bg-muted rounded px-1 py-0.5 font-mono text-xs">src/index.css</code> —
        consume tokens via Tailwind utilities (e.g., <code>bg-primary</code>) or{' '}
        <code>hsl(var(--token))</code> in CSS, never inline hex values.
      </p>
    </header>
  );
}

export default function DesignTokensRoute() {
  return (
    <div className="space-y-10 p-6">
      <PageHeader />
      {SECTIONS.map((section) => (
        <SwatchSectionView key={section.heading} section={section} />
      ))}
      <SemanticStatesSection />
      <UsageGuideSection />
      <PatternsSection />
      <ButtonsSection />
      <FormControlsSection />
      <ComboboxSection />
      <BadgesSection />
      <ChipsSection />
      <TypographySection />
      <RadiusSection />
    </div>
  );
}
